<template>
  <div id="expense-form" class="group-setup">
    <v-progress-linear
      indeterminate
      color="blue darken-2"
      v-if="loading"
    ></v-progress-linear>
    <v-layout row v-if="errors">
      <v-flex>
        <Alert @dismissed="onDismissed" :text="errors"></Alert>
      </v-flex>
    </v-layout>

    <v-form ref="expenseForm" v-model="valid" v-if="!loading">
      <v-row>
        <v-col cols="12" md="6">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Financial Year</p>
          </v-col>
          <v-col class="pa-0 ma-0">
            <v-autocomplete
              v-model="formData.financial_year"
              :items="financial_years"
              item-text="year"
              item-value="id"
              placeholder="Financial Year"
              :rules="[rules.required]"
              outlined
              dense
              clearable
            ></v-autocomplete>
          </v-col>
        </v-col>

        <v-col cols="12" md="6">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Branch</p>
          </v-col>
          <v-col class="pa-0 ma-0">
            <v-autocomplete
              v-model="formData.branch"
              :items="branches"
              item-text="name"
              item-value="id"
              placeholder="Branch"
              :rules="[rules.required]"
              outlined
              dense
              clearable
            ></v-autocomplete>
          </v-col>
        </v-col>

        <v-col cols="12" md="6">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Date</p>
          </v-col>
          <v-text-field
            v-model="formData.date_of_expense"
            placeholder="Date"
            :rules="[rules.required]"
            type="date"
            outlined
            dense
          ></v-text-field>
        </v-col>

        <v-col cols="6" md="6" sm="6">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Expense Type</p>
          </v-col>
          <v-col class="pa-0 ma-0">
            <v-autocomplete
              v-model="formData.dr_id"
              :items="expenseTypes"
              item-text="name"
              item-value="id"
              outlined
              dense
              clearable
              :rules="[rules.required]"
              :loading="isFetchingExpenseTypes"
              :filter="() => true"
              @update:search-input="
                (searchTerm) => onSearchExpenseType(searchTerm)
              "
            ></v-autocomplete>
          </v-col>
        </v-col>

        <v-col cols="6" sm="6">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Payee</p>
          </v-col>
          <v-text-field
            outlined
            dense
            v-model="formData.payee"
            required
            clearable
            :rules="[rules.required]"
            placeholder="Payee/Vendor"
          ></v-text-field>
        </v-col>

        <!-- <v-col cols="6" sm="6">
            <v-col class="pa-0 ma-0">
                <p class="text-title">Project</p>
            </v-col>
            <v-text-field
            outlined
            dense
            v-model="formData.project"
            required
            clearable
            label="Project"
            ></v-text-field>
          </v-col> -->

        <v-col cols="6" sm="6">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Amount</p>
          </v-col>
          <v-text-field
            outlined
            dense
            v-model="formData.amount"
            required
            clearable
            :rules="[rules.required]"
            placeholder="Amount"
          ></v-text-field>
        </v-col>
        <v-col cols="6" sm="6">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Payment Method</p>
          </v-col>
          <v-autocomplete
            v-model="formData.payment_method"
            :items="paymentOptions"
            item-text="name"
            item-value="id"
            placeholder="Payment Method"
            outlined
            dense
            clearable
            :rules="[rules.required]"
          ></v-autocomplete>
        </v-col>
        <!-- <v-col cols="6" md="6" sm="6" v-if="formData.payment_method === 'bank'">
            <v-col class="pa-0 ma-0">
              <p class="text-title">Bank Account</p>
            </v-col>
            <v-autocomplete
              v-model="formData.cr_id"
              :items="bank_accounts"
              item-text="account_number"
              item-value="gl_account_id"
              outlined
              dense
              clearable
              :rules="[rules.required]"
              :loading="isFetchingBankAccounts"
              :filter="() => true"
              @update:search-input="(searchTerm) => onSearchBankAccounts(searchTerm)"
            ></v-autocomplete>
          </v-col> -->

        <v-col cols="12" sm="12">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Invoices (Optional)</p>
          </v-col>
          <v-file-input
            v-model="formData.invoices"
            multiple
            accept="image/*,.pdf"
            outlined
            dense
            clearable
            placeholder="Upload Invoices"
            @change="previewImages"
            @click:clear="clearPreviews"
          ></v-file-input>

          <v-row v-if="previewUrls.length">
            <v-col
              cols="3"
              v-for="(url, index) in previewUrls"
              :key="index"
              class="mb-2"
            >
              <v-img :src="url" max-height="100px" contain></v-img>
              <v-btn small text color="red" @click="removeImage(index)"
                >Remove</v-btn
              >
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="12" sm="12">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Description</p>
          </v-col>
          <v-textarea
            v-model="formData.description"
            placeholder="Description"
            outlined
            dense
            auto-grow
          ></v-textarea>
        </v-col>
        <v-col cols="6" sm="6">
          <v-checkbox
            v-model="formData.post"
            :label="'Post'"
            :true-value="1"
            :false-value="0"
            color="primary"
            outlined
            dense
          ></v-checkbox>
        </v-col>
        <!-- Actions -->
        <v-col cols="12">
          <v-card-actions>
            <v-btn
              color="primary"
              large
              @click="submitForm"
              v-if="
                (mode === 'edit' && hasPermission('expenses.edit')) ||
                  (mode != 'edit' && hasPermission('expenses.create'))
              "
            >
              {{ mode === "edit" ? "Update Expense" : "Create Expense" }}
            </v-btn>
          </v-card-actions>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import Alert from "@/components/shared/Alert";
import { ExpenseService } from "@/services/expense-service.js";
import { checkError } from "@/config/errors.js";
import { CommonService } from "@/services/common-service.js";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");

export default {
  name: "ExpenseTypeForm",
  components: { Alert },
  props: {
    formData: { type: Object, required: true },
    mode: { type: String, required: true },
    id: { type: String, default: null },
    proploading: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: this.proploading,
      valid: false,
      errors: null,
      isFetchingExpenseTypes: false,
      isFetchingBankAccounts: false,
      ExpenseTypesSearchTimeout: null,
      BankAccountsSearchTimeout: null,
      bank_accounts: null,
      expenseTypes: [],
      previewUrls: [],
      branches: [],
      financial_years: [],
      paymentOptions: [],
      rules: {
        required: (value) => !!value || "This field is required.",
      },
    };
  },
  computed: {
    ...mapGetters({
      hasPermission: "hasPermission",
    }),
  },
  methods: {
    async submitForm() {
      if (!this.$refs.expenseForm.validate()) return;
      this.loading = true;
      try {
        const formData = new FormData();
        Object.keys(this.formData).forEach((key) => {
          if (key === "invoices") {
            // Append multiple files
            this.formData.invoices?.forEach((file, index) => {
              formData.append(`invoices[${index}]`, file);
            });
          } else {
            formData.append(key, this.formData[key]);
          }
        });

        if (this.mode === "create") {
          await ExpenseService.createExpense(formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
          await ExpenseService.updateExpense(this.id, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }
        this.$router.push({ name: "ExpenseList" });
      } catch (error) {
        console.error("Error object logged", error);
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      } finally {
        this.loading = false;
      }
    },

    async fetchDropdownData() {
      try {
        const financialYearsResponse = await CommonService.getFinancialYears();
        this.financial_years = financialYearsResponse.data;
        const branchesResponse = await CommonService.getBranches();
        this.branches = branchesResponse.data;
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      }
    },

    async onSearchExpenseType(searchTerm) {
      try {
        clearTimeout(this.ExpenseTypesSearchTimeout);
        this.isFetchingExpenseTypes = true;
        this.ExpenseTypesSearchTimeout = setTimeout(async () => {
          const response = await ExpenseService.getExpenseTypes(searchTerm);
          this.expenseTypes = response.data.data;
        }, 300);
      } catch (error) {
        console.error("Error fetching accounts:", error);
      } finally {
        this.isFetchingExpenseTypes = false;
      }
    },

    async onSearchBankAccounts(searchTerm) {
      try {
        clearTimeout(this.BankAccountsSearchTimeout);
        this.isFetchingBankAccounts = true;
        this.BankAccountsSearchTimeout = setTimeout(async () => {
          const response = await ExpenseService.getBanks(searchTerm);
          this.bank_accounts = response.data;
          console.log("bank accounts : ", this.bank_accounts);
        }, 300);
      } catch (error) {
        console.error("Error fetching Bank Accounts:", error);
      } finally {
        this.isFetchingBankAccounts = false;
      }
    },

    async fetchPaymentMethods() {
      try {
        const response = await ExpenseService.getPaymentMethods();
        this.paymentOptions = response.data;
      } catch (error) {
        console.error("Error fetching payment methods:", error);
      }
    },

    previewImages() {
      this.previewUrls.forEach((url) => URL.revokeObjectURL(url));
      this.previewUrls = this.formData.invoices.map((file) =>
        URL.createObjectURL(file)
      );
    },

    removeImage(index) {
      URL.revokeObjectURL(this.previewUrls[index]);
      this.formData.invoices.splice(index, 1);
      this.previewUrls.splice(index, 1);
    },

    clearPreviews() {
      // Revoke all object URLs when the clear icon is clicked
      this.previewUrls.forEach((url) => URL.revokeObjectURL(url));
      this.previewUrls = [];
    },

    onDismissed() {
      this.errors = null;
    },
  },
  watch: {
    "formData.dr_id"(newId) {
      const selectedType = this.expenseTypes.find((type) => type.id === newId);
      if (selectedType) {
        this.formData.dr_id = selectedType.gl_account_id;
      } else {
        this.formData.gl_account_id = null;
      }
    },
    proploading(newVal) {
      this.loading = newVal;
    },
  },
  beforeDestroy() {
    this.previewUrls.forEach((url) => URL.revokeObjectURL(url));
  },
  async mounted() {
    await this.fetchDropdownData();
    await this.fetchPaymentMethods();
    await this.onSearchExpenseType("");
  },
};
</script>
